import React from 'react';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell, LabelList } from 'recharts';
import { Trophy, MapPin } from 'lucide-react';
import { buildApiaryYield } from '../../utils/harvestStats';

export const ApiaryYieldComparison = ({ harvests, hives, unit = 'kg', subtitle, highlight, testId = 'apiary-yield-comparison', headerRight }) => {
  const stats = buildApiaryYield(harvests, hives, unit);
  const best = stats.length > 1 && stats[0].perHive > 0 ? stats[0].apiary : null;
  return (
    <div data-testid={testId} className="bg-white rounded-xl border border-amber-200 p-4">
      <div className="flex flex-wrap items-start justify-between gap-2 mb-3">
        <div>
          <h3 className="font-semibold text-amber-900 flex items-center gap-2"><Trophy className="h-4 w-4 text-amber-600" /> Apiary Yield Comparison</h3>
          <p className="text-xs text-stone-500">{unit} per hive = total {unit} ÷ hives harvested{subtitle ? ` · ${subtitle}` : ''}</p>
        </div>
        {headerRight}
      </div>
      {stats.length === 0 ? (
        <p className="text-sm text-stone-400 italic py-4 text-center">No harvests in this season yet.</p>
      ) : (
        <div className="grid lg:grid-cols-5 gap-4">
          <div className="lg:col-span-2 h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stats} margin={{ top: 18, right: 8, left: -10, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f5f5f4" vertical={false} />
                <XAxis dataKey="apiary" tick={{ fontSize: 11 }} interval={0} />
                <YAxis tick={{ fontSize: 11 }} unit={unit} />
                <Tooltip formatter={(v) => [`${v} ${unit}`, 'per hive']} />
                <Bar dataKey="perHive" radius={[6, 6, 0, 0]}>
                  <LabelList dataKey="perHive" position="top" fontSize={11} fill="#78350f" formatter={(v) => `${v}`} />
                  {stats.map(s => <Cell key={s.apiary} fill={s.apiary === best ? '#d97706' : '#fcd34d'} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="lg:col-span-3 grid sm:grid-cols-2 gap-2 content-start">
            {stats.map((s, i) => (
              <div key={s.apiary} data-testid={`${testId}-card-${i}`} className={`rounded-lg border p-3 ${s.apiary === best ? 'border-amber-400 bg-amber-50' : highlight === s.apiary ? 'border-teal-300 bg-teal-50/40' : 'border-stone-200'}`}>
                <div className="flex items-center justify-between gap-2">
                  <span className="font-medium text-stone-800 flex items-center gap-1 truncate"><MapPin className="h-3.5 w-3.5 text-teal-600 shrink-0" />{s.apiary}</span>
                  {s.apiary === best && <span data-testid={`${testId}-best`} className="text-[10px] bg-amber-600 text-white px-2 py-0.5 rounded-full flex items-center gap-1 shrink-0"><Trophy className="h-3 w-3" />Most productive</span>}
                </div>
                <div className="flex items-baseline gap-1 mt-1">
                  <span className="text-2xl font-bold text-amber-700">{s.perHive}</span>
                  <span className="text-xs text-stone-500">{unit} / hive</span>
                </div>
                <p className="text-xs text-stone-500">{s.total} {unit} total · {s.hives} hive{s.hives !== 1 ? 's' : ''} · {s.harvests} harvest{s.harvests !== 1 ? 's' : ''}</p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};
